import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { tokenStore } from "./tokenStore";
import { refreshAccessToken } from "./authRefresh";

const appendQueryParam = (base: string, key: string, value: string) => {
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${key}=${encodeURIComponent(value)}`;
};

const buildUrlWithPatientId = (base: string, patientId?: string) => {
    if (!patientId || !patientId.trim()) return base;
    return appendQueryParam(base, "patient_id", patientId.trim());
};

const getApiBaseUrl = (): string | undefined => {
    const envURL = process.env.REACT_APP_BASE_URL;
    if (envURL && envURL.trim().length) return envURL;
    if (typeof window !== "undefined") {
        const { protocol, hostname } = window.location;
        return `${protocol}//${hostname}:1245`;
    }
    return undefined;
};

// The chat WebSocket requires a valid auth ticket (issue #6). The browser
// WebSocket API can't set Authorization headers, and passing the JWT itself
// as a query param leaks it into access logs and browser history. Instead we
// exchange our in-memory access token for a single-use, short-lived ticket
// (POST /auth/ws-ticket) right before connecting. If the access token has
// expired (they are now short-lived), we silently refresh it once and retry.
const fetchWsTicket = async (): Promise<string | null> => {
    for (let attempt = 0; attempt < 2; attempt++) {
        const token = tokenStore.get();
        const base = getApiBaseUrl();
        if (!token || !base) return null;
        try {
            const response = await axios.post(
                `${base}/api/v1/auth/ws-ticket`,
                undefined,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                }
            );
            return response.data?.ticket ?? null;
        } catch (err: any) {
            if (attempt === 0 && err?.response?.status === 401) {
                // Access token expired — silently refresh it (shared/deduped
                // with every other 401 handler in the app) and retry once.
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) continue;
                return null;
            }
            return null;
        }
    }
    return null;
};

const getWebsocketURL = () => {
    const envURL = process.env.REACT_APP_ORCHESTRATOR_WEBSOCKET_URL;

    if (envURL && envURL.trim().length) {
        return envURL;
    }

    if (typeof window !== "undefined") {
        const isSecure = window.location.protocol === "https:";
        const protocol = isSecure ? "wss" : "ws";
        const hostname = window.location.hostname;
        return `${protocol}://${hostname}:1252/api/v1/chat/live/ws/chat`;
    }

    return "ws://localhost:1252/api/v1/chat/live/ws/chat";
};

export interface Guideline {
    text: string;
    source_img: string;
    chunk_id?: string;
    page_no?: number;
    short_title?: string;
}

// Provenance for one recalled memory fact: the originating chat turn it was
// extracted from. `content` is the clinician's original message, quotable as
// "where this came from". Shape matches orchestrator's serialized memory_facts.
export interface MemoryFactSource {
    content: string;
    timestamp?: string | null;
    conversation_id?: string | null;
    message_id?: string | null;
    source_description?: string | null;
}

// One cross-conversation patient-memory fact recalled for an answer, with its
// sources. "Recalled" (retrieved for the turn), not necessarily "cited" by the
// model — the UI wording reflects that.
export interface MemoryFact {
    // uuid/name/expired_at are populated by the fact-management endpoints (the Facts
    // panel); they're absent from the chat-stream payload, hence optional.
    uuid?: string | null;
    name?: string | null;
    fact: string;
    valid_at?: string | null;
    invalid_at?: string | null;
    expired_at?: string | null;
    relevance?: number | null;
    source_episode_ids?: string[];
    sources: MemoryFactSource[];
}

type ServerMsg =
    | { type: "start" }
    | { type: "token"; data: string }
    | { type: "end"; guidelines?: Array<{guideline_chunk_id: string; guideline_document_id: string; guideline_image: string}>; memory_facts?: MemoryFact[] }
    | { type: "stopping" }
    | { type: "summarizing" }
    | { type: "recalling_memory" }
    | { type: "thinking" }
    | { type: "guidelines"; guidelines: Guideline[] }
    | { type: "summary"; data?: string }
    | { type: "error"; data?: string }
    | { type: "heartbeat" };

type Options = {
    url?: string;
    patientId?: string;
    autoStart?: boolean;
    autoReconnect?: boolean;
    maxRetries?: number;
    retryBaseDelayMs?: number;
    flushIntervalMs?: number;
    onSummary?: (summary: string) => void;
};

export function useChatStream({
    url,
    patientId,
    autoStart = true,
    autoReconnect = true,
    maxRetries = 5,
    retryBaseDelayMs = 400,
    onSummary,
}: Options) {
    const resolvedUrl = useMemo(() => {
        const base = url && url.trim().length ? url : getWebsocketURL();
        // No token here — a single-use ticket is fetched per connection
        // attempt (see connect) so nothing long-lived lands in the URL.
        return buildUrlWithPatientId(base, patientId);
    }, [url, patientId]);

    const [text, setText] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [isRecallingMemory, setIsRecallingMemory] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [guidelines, setGuidelines] = useState<Guideline[]>([]);

    const wsRef = useRef<WebSocket | null>(null);
    const userStoppedRef = useRef(false);

    // Stable ref for onSummary – avoids making `connect` depend on the prop
    // (an inline arrow function would recreate `connect` on every render and
    // trigger the cleanup effect, killing the live socket).
    const onSummaryRef = useRef(onSummary);
    useEffect(() => { onSummaryRef.current = onSummary; }, [onSummary]);
    const retriesRef = useRef(0);

    const bufferRef = useRef<string>("");
    const flushTimerRef = useRef<number | null>(null);

    const clearFlushTimer = () => {
        if (flushTimerRef.current !== null) {
            window.cancelAnimationFrame(flushTimerRef.current);
            flushTimerRef.current = null;
        }
    };

    const flushBuffer = useCallback(() => {
        flushTimerRef.current = null;
        if (!bufferRef.current) return;
        const chunk = bufferRef.current;
        bufferRef.current = "";
        setText((t) => t + chunk);
    }, []);

    const scheduleFlush = useCallback(() => {
        if (flushTimerRef.current !== null) return;
        flushTimerRef.current = window.requestAnimationFrame(flushBuffer);
    }, [flushBuffer]);

    const reset = useCallback(() => {
        setText("");
        setError(null);
        setHasEnded(false);
        setIsStreaming(false);
        setIsSummarizing(false);
        setIsRecallingMemory(false);
        setIsThinking(false);
        setGuidelines([]);
        bufferRef.current = "";
        clearFlushTimer();
        userStoppedRef.current = false;
    }, []);

    const cleanUpSocket = useCallback(() => {
        const ws = wsRef.current;
        if (ws) {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            try {
                ws.close();
            } catch { }
        }
        wsRef.current = null;
        clearFlushTimer();
    }, []);

    const stop = useCallback(() => {
        userStoppedRef.current = true;
        setIsStreaming(false);
        try {
            wsRef.current?.send(JSON.stringify({ type: "stop" }));
        } catch { }
        // Keep socket open to receive any final events (end/summary).
    }, []);

    /** Connect (or reconnect) to the WebSocket server */
    const connect: () => void = useCallback(() => {
        setError(null);
        setHasEnded(false);
        setIsStreaming(false);
        userStoppedRef.current = false;

        if (
            wsRef.current &&
            (wsRef.current.readyState === WebSocket.OPEN ||
                wsRef.current.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        // Fetch a single-use auth ticket first; the WS URL itself never
        // carries the JWT (issue #6).
        void fetchWsTicket().then((ticket) => {
            if (
                wsRef.current &&
                (wsRef.current.readyState === WebSocket.OPEN ||
                    wsRef.current.readyState === WebSocket.CONNECTING)
            ) {
                return;
            }
            const url = ticket
                ? appendQueryParam(resolvedUrl, "ticket", ticket)
                : resolvedUrl;
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                retriesRef.current = 0;
            };

            ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data) as ServerMsg;
                switch (msg.type) {
                    case "summarizing":
                        setIsSummarizing(true);
                        setIsRecallingMemory(false);
                        setIsThinking(false);
                        break;
                    case "recalling_memory":
                        setIsSummarizing(false);
                        setIsRecallingMemory(true);
                        setIsThinking(false);
                        break;
                    case "thinking":
                        setIsSummarizing(false);
                        setIsRecallingMemory(false);
                        setIsThinking(true);
                        break;
                    case "start":
                        setIsSummarizing(false);
                        setIsRecallingMemory(false);
                        setIsThinking(false);
                        setIsStreaming(true);
                        setHasEnded(false);
                        break;
                    case "guidelines":
                        // Receive filtered guidelines after stream completes
                        if (Array.isArray(msg.guidelines) && msg.guidelines.length > 0) {
                            setGuidelines(msg.guidelines);
                        }
                        break;
                    case "token":
                        if (typeof msg.data === "string" && msg.data.length) {
                            // Clear all loading states when first token arrives
                            setIsSummarizing(false);
                            setIsRecallingMemory(false);
                            setIsThinking(false);
                            setIsStreaming(true);
                            bufferRef.current += msg.data;
                            scheduleFlush();
                        }
                        break;
                    case "stopping":
                        break;
                    case "end":
                        flushBuffer();
                        setIsSummarizing(false);
                        setIsRecallingMemory(false);
                        setIsThinking(false);
                        setIsStreaming(false);
                        setHasEnded(true);
                        // Log guidelines received from gRPC server
                        if (msg.guidelines && msg.guidelines.length > 0) {
                        } else {
                        }
                        // cleanUpSocket();
                        break;
                    case "error":
                        setError(msg.data || "Unknown server error");
                        setIsSummarizing(false);
                        setIsRecallingMemory(false);
                        setIsThinking(false);
                        setIsStreaming(false);
                        setHasEnded(true);
                        cleanUpSocket();
                        break;
                        case "heartbeat":
                            // server keepalive – no UI action needed
                            break;
                        case "summary":
                            if (msg.data) onSummaryRef.current?.(msg.data);
                            break;
                }
            } catch {
                /* ignore */
            }
        };

        ws.onerror = () => setError("WebSocket error");

        ws.onclose = () => {
            setIsStreaming(false);
            if (userStoppedRef.current) return;

            if (autoReconnect && retriesRef.current < maxRetries) {
                const attempt = ++retriesRef.current;
                const delay = Math.min(
                    8000,
                    retryBaseDelayMs * Math.pow(2, attempt - 1)
                );
                setTimeout(() => connect(), delay);
            } else {
                setError((e) => e ?? "Connection closed");
            }
        };
        }); // end fetchWsTicket().then(...)
    }, [
        autoReconnect,
        cleanUpSocket,
        maxRetries,
        retryBaseDelayMs,
        resolvedUrl,
        // onSummary intentionally omitted – accessed via onSummaryRef to keep
        // this callback stable even when the prop is an inline arrow function.
    ]);

    

    const _sendWhenOpen = useCallback((payload: any) => {
        const send = () => wsRef.current?.send(JSON.stringify(payload));
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSING || wsRef.current.readyState === WebSocket.CLOSED) {
            connect();
            const onOpenOnce = () => {
                wsRef.current?.removeEventListener("open", onOpenOnce as any);
                send();
            };
            wsRef.current?.addEventListener("open", onOpenOnce as any);
        } else if (wsRef.current.readyState === WebSocket.CONNECTING) {
            const onOpenOnce = () => {
                wsRef.current?.removeEventListener("open", onOpenOnce as any);
                send();
            };
            wsRef.current.addEventListener("open", onOpenOnce as any);
        } else {
            send();
        }
    }, [connect]);

    const ask = useCallback((text: string, conversationId?: string | null, replyId?: string | null, runId?: string | null, model?: string | null) => {
        setError(null);
        setHasEnded(false);
        // setIsStreaming(true);
        setText("");
        // Immediately show a neutral retrieving state for instant feedback.
        // The client can't know whether patient memory will actually be
        // recalled, so show "thinking"; the server upgrades this to
        // "recalling_memory" only when memory recall really runs.
        setIsSummarizing(false);
        setIsRecallingMemory(false);
        setIsThinking(true);
        // Don't clear guidelines here - they should persist in history
        // setGuidelines([]);
        const payload = {
            type: "ask",
            text,
            conversation_id: conversationId ?? null,
            reply_id: replyId ?? null,
            run_id: runId ?? null,
            model: model ?? null,
        };
        _sendWhenOpen(payload);
    }, [_sendWhenOpen]);

    useEffect(() => {
        if (autoStart) connect();
        return () => cleanUpSocket();
    }, [autoStart, connect, cleanUpSocket]);

    return useMemo(
        () => ({
            text,
            isStreaming,
            isSummarizing,
            isRecallingMemory,
            isThinking,
            hasEnded,
            error,
            guidelines,
            connect,
            stop,
            reset,
            ask
        }),
        [text, isStreaming, isSummarizing, isRecallingMemory, isThinking, hasEnded, error, guidelines, connect, stop, reset, ask]
    );
}


// ── Per-conversation stream controller ──────────────────────────────────────
// Plain (non-React) controller that owns a single WebSocket for ONE
// conversation. It is created and held by ChatStreamProvider (mounted above the
// router), so an in-flight generation survives the Chat component being
// unmounted/remounted as the user navigates in and out of the chat. Mirrors the
// message handling of `useChatStream` above, but exposes a subscribe/getSnapshot
// surface suitable for React's useSyncExternalStore.

export interface StreamSnapshot {
    text: string;
    isStreaming: boolean;
    isSummarizing: boolean;
    isRecallingMemory: boolean;
    isThinking: boolean;
    hasEnded: boolean;
    error: string | null;
    guidelines: Guideline[];
    memoryFacts: MemoryFact[];
}

export const EMPTY_SNAPSHOT: StreamSnapshot = {
    text: "",
    isStreaming: false,
    isSummarizing: false,
    isRecallingMemory: false,
    isThinking: false,
    hasEnded: false,
    error: null,
    guidelines: [],
    memoryFacts: [],
};

// "Active" = the model is still working on this turn (any pre-token phase or
// actively streaming) and it has not ended. Drives both the composer lock and
// the sidebar "generating" indicator.
export const isSnapshotActive = (s: StreamSnapshot): boolean =>
    !s.hasEnded &&
    (s.isStreaming || s.isSummarizing || s.isRecallingMemory || s.isThinking);

export type StartStreamOptions = {
    text: string;
    conversationId?: string | null;
    replyId?: string | null;
    runId?: string | null;
    model?: string | null;
};

type ControllerOptions = {
    url?: string;
    patientId?: string;
    autoReconnect?: boolean;
    maxRetries?: number;
    retryBaseDelayMs?: number;
};

export class ConversationStreamController {
    private ws: WebSocket | null = null;
    private userStopped = false;
    private retries = 0;
    private buffer = "";
    private flushTimer: number | null = null;
    private listeners = new Set<() => void>();
    private snapshot: StreamSnapshot = EMPTY_SNAPSHOT;
    private readonly resolvedUrl: string;
    private readonly autoReconnect: boolean;
    private readonly maxRetries: number;
    private readonly retryBaseDelayMs: number;
    private pendingPayload: Record<string, unknown> | null = null;
    private disposed = false;

    // Set by whichever component is currently viewing this conversation, so a
    // late `summary` (delivered after `end`) can update the sidebar title while
    // mounted. A summary that arrives while nothing is viewing this conversation
    // is simply dropped — the title is also refreshed from the DB on next load.
    onSummary: ((summary: string) => void) | null = null;

    constructor(opts: ControllerOptions) {
        const base = opts.url && opts.url.trim().length ? opts.url : getWebsocketURL();
        this.resolvedUrl = buildUrlWithPatientId(base, opts.patientId);
        this.autoReconnect = opts.autoReconnect ?? true;
        this.maxRetries = opts.maxRetries ?? 5;
        this.retryBaseDelayMs = opts.retryBaseDelayMs ?? 400;
    }

    subscribe = (cb: () => void): (() => void) => {
        this.listeners.add(cb);
        return () => {
            this.listeners.delete(cb);
        };
    };

    getSnapshot = (): StreamSnapshot => this.snapshot;

    get isActive(): boolean {
        return isSnapshotActive(this.snapshot);
    }

    private set(patch: Partial<StreamSnapshot>) {
        this.snapshot = { ...this.snapshot, ...patch };
        this.listeners.forEach((l) => l());
    }

    private clearFlushTimer() {
        if (this.flushTimer !== null) {
            window.cancelAnimationFrame(this.flushTimer);
            this.flushTimer = null;
        }
    }

    private flushBuffer = () => {
        this.flushTimer = null;
        if (!this.buffer) return;
        const chunk = this.buffer;
        this.buffer = "";
        this.set({ text: this.snapshot.text + chunk });
    };

    private scheduleFlush() {
        if (this.flushTimer !== null) return;
        this.flushTimer = window.requestAnimationFrame(this.flushBuffer);
    }

    /** Start a new turn on this conversation. */
    ask(opts: StartStreamOptions) {
        this.userStopped = false;
        this.buffer = "";
        this.clearFlushTimer();
        // Fresh "thinking" state for the new turn. Show a neutral thinking state
        // immediately; the server upgrades it (recalling_memory/summarizing) or
        // begins streaming tokens.
        this.snapshot = { ...EMPTY_SNAPSHOT, isThinking: true };
        this.listeners.forEach((l) => l());
        const payload = {
            type: "ask",
            text: opts.text,
            conversation_id: opts.conversationId ?? null,
            reply_id: opts.replyId ?? null,
            run_id: opts.runId ?? null,
            model: opts.model ?? null,
        };
        this.sendWhenOpen(payload);
    }

    /**
     * User-requested stop. Interrupts the request and finalizes immediately on
     * the client, rather than waiting for a server `end`. The server also
     * cancels generation + persists the partial when it receives `{type:"stop"}`.
     */
    stop() {
        if (this.snapshot.hasEnded) return; // already finalized; idempotent
        this.userStopped = true;
        // If the `ask` was queued but not yet delivered, drop it so it never fires.
        this.pendingPayload = null;
        // Surface any tokens already buffered before we freeze.
        this.flushBuffer();
        const ws = this.ws;
        if (ws && ws.readyState === WebSocket.OPEN) {
            // Tell the server to cancel the in-flight generation.
            try {
                ws.send(JSON.stringify({ type: "stop" }));
            } catch {
                /* ignore */
            }
        } else {
            // Socket not open (still CONNECTING, or already gone): the `ask`
            // never reached the server, so nothing is running — just tear down.
            this.cleanUpSocket();
        }
        // Finalize now: drop every "active" flag so isSnapshotActive() is false
        // (composer unlocks) and drive the normal end path. Late server messages
        // are ignored via the userStopped guard in onmessage.
        this.set({
            isStreaming: false,
            isThinking: false,
            isSummarizing: false,
            isRecallingMemory: false,
            hasEnded: true,
        });
    }

    /** Tear down permanently (provider retirement / logout / app unmount). */
    dispose() {
        this.disposed = true;
        this.onSummary = null;
        this.listeners.clear();
        this.cleanUpSocket();
    }

    private cleanUpSocket() {
        const ws = this.ws;
        if (ws) {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            try {
                ws.close();
            } catch {
                /* ignore */
            }
        }
        this.ws = null;
        this.clearFlushTimer();
    }

    private sendWhenOpen(payload: Record<string, unknown>) {
        const ws = this.ws;
        if (!ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
            this.pendingPayload = payload;
            this.connect();
        } else if (ws.readyState === WebSocket.CONNECTING) {
            this.pendingPayload = payload;
        } else {
            try {
                ws.send(JSON.stringify(payload));
            } catch {
                /* ignore */
            }
        }
    }

    private connect() {
        if (this.disposed) return;
        if (
            this.ws &&
            (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        // Fetch a single-use auth ticket first; the WS URL itself never
        // carries the JWT (issue #6).
        void fetchWsTicket().then((ticket) => {
            if (this.disposed) return;
            if (
                this.ws &&
                (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
            ) {
                return;
            }
            const url = ticket
                ? appendQueryParam(this.resolvedUrl, "ticket", ticket)
                : this.resolvedUrl;
            this.openSocket(url);
        });
    }

    private openSocket(url: string) {
        const ws = new WebSocket(url);
        this.ws = ws;

        ws.onopen = () => {
            this.retries = 0;
            if (this.pendingPayload) {
                const p = this.pendingPayload;
                this.pendingPayload = null;
                try {
                    ws.send(JSON.stringify(p));
                } catch {
                    /* ignore */
                }
            }
        };

        ws.onmessage = (evt) => {
            let msg: ServerMsg;
            try {
                msg = JSON.parse(evt.data) as ServerMsg;
            } catch {
                return;
            }
            // After a user stop we've already finalized; ignore any further
            // streaming/phase messages so late tokens can't un-freeze the entry.
            // A late `summary` is still allowed through to update the title.
            if (this.userStopped && msg.type !== "summary") return;
            switch (msg.type) {
                case "summarizing":
                    this.set({ isSummarizing: true, isRecallingMemory: false, isThinking: false });
                    break;
                case "recalling_memory":
                    this.set({ isSummarizing: false, isRecallingMemory: true, isThinking: false });
                    break;
                case "thinking":
                    this.set({ isSummarizing: false, isRecallingMemory: false, isThinking: true });
                    break;
                case "start":
                    this.set({
                        isSummarizing: false,
                        isRecallingMemory: false,
                        isThinking: false,
                        isStreaming: true,
                        hasEnded: false,
                        // Clear the previous turn's recalled facts so a new answer never
                        // shows stale provenance; the `end` frame repopulates this turn's.
                        // (Persisted facts still live in fetched history, unaffected.)
                        memoryFacts: [],
                    });
                    break;
                case "guidelines":
                    if (Array.isArray(msg.guidelines) && msg.guidelines.length > 0) {
                        this.set({ guidelines: msg.guidelines });
                    }
                    break;
                case "token":
                    if (typeof msg.data === "string" && msg.data.length) {
                        if (
                            this.snapshot.isThinking ||
                            this.snapshot.isSummarizing ||
                            this.snapshot.isRecallingMemory ||
                            !this.snapshot.isStreaming
                        ) {
                            this.set({
                                isSummarizing: false,
                                isRecallingMemory: false,
                                isThinking: false,
                                isStreaming: true,
                            });
                        }
                        this.buffer += msg.data;
                        this.scheduleFlush();
                    }
                    break;
                case "stopping":
                    break;
                case "end":
                    this.flushBuffer();
                    this.set({
                        isSummarizing: false,
                        isRecallingMemory: false,
                        isThinking: false,
                        isStreaming: false,
                        hasEnded: true,
                        ...(Array.isArray(msg.memory_facts) && msg.memory_facts.length > 0
                            ? { memoryFacts: msg.memory_facts }
                            : {}),
                    });
                    break;
                case "error":
                    this.set({
                        error: msg.data || "Unknown server error",
                        isSummarizing: false,
                        isRecallingMemory: false,
                        isThinking: false,
                        isStreaming: false,
                        hasEnded: true,
                    });
                    this.cleanUpSocket();
                    break;
                case "heartbeat":
                    break;
                case "summary":
                    if (msg.data) this.onSummary?.(msg.data);
                    break;
            }
        };

        ws.onerror = () => this.set({ error: "WebSocket error" });

        ws.onclose = () => {
            const wasActive = isSnapshotActive(this.snapshot);
            if (this.snapshot.isStreaming) this.set({ isStreaming: false });
            if (this.userStopped || this.disposed) return;

            // Only reconnect to (re)deliver a queued ask; reconnecting cannot
            // resume a generation the server already aborted on disconnect.
            if (this.pendingPayload && this.autoReconnect && this.retries < this.maxRetries) {
                const attempt = ++this.retries;
                const delay = Math.min(8000, this.retryBaseDelayMs * Math.pow(2, attempt - 1));
                setTimeout(() => this.connect(), delay);
                return;
            }

            // An unexpected drop while a turn was in progress: mark it ended so
            // the UI stops waiting (and the provider retires the controller).
            if (wasActive && !this.snapshot.hasEnded) {
                this.set({ error: this.snapshot.error ?? "Connection closed", hasEnded: true });
            }
        };
    }
}

